// Edytor artykułu bloga CMS

import React, { FC, useEffect, useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowLeft, BoxArrowUpRight, FileEarmarkText, Image as ImageIcon, Gear, Globe } from 'react-bootstrap-icons';

import {
  useGetCmsArticle,
  usePutCmsArticle,
  usePostCmsArticle,
  useGetCmsArticleCountries,
  useGetCmsSectionFields,
  useGetSeoPageInfo,
  usePutSeoPageInfo
} from 'api';
import {
  Button,
  Checkbox,
  Loader,
  Modal,
  TranslatedEditor,
  TranslatedFile
} from 'components/controls';

import styles from 'theme/pages/PanelBlog/BlogEditor.module.scss';

const SECTION_ID = 65;

// Field IDs (from /cms/section/65/fields) - used as fallback; overwritten from API data
const FIELD_SYMBOL_TITLE = 'title';
const FIELD_SYMBOL_SUBTITLE = 'Podtytuł';
const FIELD_SYMBOL_IMAGE = 'image';
const FIELD_SYMBOL_CONTENT = 'content';
const FIELD_SYMBOL_POPULAR = 'popular';
const FIELD_SYMBOL_TUTORIAL = 'tutorial';
const FIELD_SYMBOL_NEWS = 'news';

interface ITranslation {
  language: string;
  translation: string;
}

interface IProps {
  articleId?: number;
  onBack: () => void;
  onSaved: (newId?: number) => void;
}

// ── SEO Modal ──────────────────────────────────────────────────────────────────

interface ISeoModalProps {
  language: string;
  countries: any[];
  seoUrlShortcut: ITranslation[];
  setSeoUrlShortcut: (v: ITranslation[]) => void;
  seoH1: ITranslation[];
  setSeoH1: (v: ITranslation[]) => void;
  seoMetaTitle: ITranslation[];
  setSeoMetaTitle: (v: ITranslation[]) => void;
  seoMetaDescription: ITranslation[];
  setSeoMetaDescription: (v: ITranslation[]) => void;
  seoMetaKeywords: ITranslation[];
  setSeoMetaKeywords: (v: ITranslation[]) => void;
  seoOgTitle: ITranslation[];
  setSeoOgTitle: (v: ITranslation[]) => void;
  seoOgDescription: ITranslation[];
  setSeoOgDescription: (v: ITranslation[]) => void;
  isSaving: boolean;
  onSave: () => void;
  onClose: () => void;
}

const SeoModal: FC<ISeoModalProps> = ({
  language: initialLang,
  countries,
  seoUrlShortcut, setSeoUrlShortcut,
  seoH1, setSeoH1,
  seoMetaTitle, setSeoMetaTitle,
  seoMetaDescription, setSeoMetaDescription,
  seoMetaKeywords, setSeoMetaKeywords,
  seoOgTitle, setSeoOgTitle,
  seoOgDescription, setSeoOgDescription,
  isSaving,
  onSave,
  onClose
}) => {
  const { t } = useTranslation();
  const [lang, setLang] = useState(initialLang);

  const get = (arr: ITranslation[]) => arr.find((x) => x.language === lang)?.translation || '';
  const set = (arr: ITranslation[], setter: (v: ITranslation[]) => void, val: string) =>
    setter(arr.map((x) => (x.language === lang ? { ...x, translation: val } : x)));

  const handleSave = async () => {
    await onSave();
    onClose();
  };

  return (
    <Modal title={t('Ustawienia SEO')} fullWidth onClose={onClose}>
      <div className={styles.seoModal}>

        {/* Language tabs */}
        <div className={styles.seoModalLangTabs}>
          {countries.map((c: any) => (
            <button
              key={c.code}
              className={`${styles.langTab} ${lang === c.code ? styles.langTabActive : ''}`}
              onClick={() => setLang(c.code)}>
              {c.code.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Fields grid */}
        <div className={styles.seoModalGrid}>

          <div className={styles.fieldGroup}>
            <label className={styles.label}><Trans>URL</Trans></label>
            <input className={styles.input} value={get(seoUrlShortcut)}
              onChange={(e) => set(seoUrlShortcut, setSeoUrlShortcut, e.target.value)} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}><Trans>H1</Trans></label>
            <input className={styles.input} value={get(seoH1)}
              onChange={(e) => set(seoH1, setSeoH1, e.target.value)} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}><Trans>Meta title</Trans></label>
            <input className={styles.input} value={get(seoMetaTitle)}
              onChange={(e) => set(seoMetaTitle, setSeoMetaTitle, e.target.value)} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}><Trans>Meta description</Trans></label>
            <textarea className={styles.textarea} rows={3} value={get(seoMetaDescription)}
              onChange={(e) => set(seoMetaDescription, setSeoMetaDescription, e.target.value)} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}><Trans>Meta keywords</Trans></label>
            <input className={styles.input} value={get(seoMetaKeywords)}
              onChange={(e) => set(seoMetaKeywords, setSeoMetaKeywords, e.target.value)} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}><Trans>OG title</Trans></label>
            <input className={styles.input} value={get(seoOgTitle)}
              onChange={(e) => set(seoOgTitle, setSeoOgTitle, e.target.value)} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}><Trans>OG description</Trans></label>
            <textarea className={styles.textarea} rows={3} value={get(seoOgDescription)}
              onChange={(e) => set(seoOgDescription, setSeoOgDescription, e.target.value)} />
          </div>

        </div>

        {/* Actions */}
        <div className={styles.seoModalActions}>
          <Button color="gray" onClick={onClose}><Trans>Anuluj</Trans></Button>
          <Button color="primary" loading={isSaving} onClick={handleSave}>
            <Trans>Zapisz SEO</Trans>
          </Button>
        </div>

      </div>
    </Modal>
  );
};

const BlogEditor: FC<IProps> = ({ articleId, onBack, onSaved }) => {
  const { t } = useTranslation();
  const isNew = !articleId;

  const [language, setLanguage] = useState('pl');

  const { data: countries } = useGetCmsArticleCountries();
  const { data: sectionFields } = useGetCmsSectionFields(
    SECTION_ID,
    { page: 1, limit: 100 },
    { enabled: isNew }
  );

  const { data: articleData, isLoading } = useGetCmsArticle(
    articleId || 0,
    undefined,
    { enabled: !!articleId, cacheTime: 0, staleTime: 0 }
  );

  const { mutateAsync: updateArticle, isLoading: isSaving } = usePutCmsArticle(articleId || 0);
  const { mutateAsync: createArticle, isLoading: isCreating } = usePostCmsArticle();

  // ── SEO ───────────────────────────────────────────────────────────────────
  const { data: seoData, refetch: refetchSeo } = useGetSeoPageInfo(
    { limit: 1, pageType: 'ARTICLE_BLOG', entityType: 'ARTICLE', entityId: articleId },
    { enabled: !isNew && !!articleId }
  );
  const seoItem = seoData?.items?.[0];
  const { mutateAsync: updateSeo, isLoading: isSavingSeo } = usePutSeoPageInfo(seoItem?.id || 0);

  // ── Form state ────────────────────────────────────────────────────────────
  const [symbol, setSymbol] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [popular, setPopular] = useState(false);
  const [tutorial, setTutorial] = useState(false);
  const [news, setNews] = useState(false);

  const [titleValues, setTitleValues] = useState<ITranslation[]>([]);
  const [subtitleValues, setSubtitleValues] = useState<ITranslation[]>([]);
  const [contentValues, setContentValues] = useState<ITranslation[]>([]);

  // imageFile stores file array (with URLs), imageValues stores value array
  const [imageFile, setImageFile] = useState<any[]>([]);
  const [imageValues, setImageValues] = useState<any[]>([]);

  // section_field_ids - resolved from article_fields or from sectionFields (new article)
  const [titleFieldId, setTitleFieldId] = useState<number | null>(null);
  const [subtitleFieldId, setSubtitleFieldId] = useState<number | null>(null);
  const [imageFieldId, setImageFieldId] = useState<number | null>(null);
  const [contentFieldId, setContentFieldId] = useState<number | null>(null);
  const [popularFieldId, setPopularFieldId] = useState<number | null>(null);
  const [tutorialFieldId, setTutorialFieldId] = useState<number | null>(null);
  const [newsFieldId, setNewsFieldId] = useState<number | null>(null);

  // SEO state
  const [seoModalOpen, setSeoModalOpen] = useState(false);
  const [seoUrlShortcut, setSeoUrlShortcut] = useState<ITranslation[]>([]);
  const [seoH1, setSeoH1] = useState<ITranslation[]>([]);
  const [seoMetaTitle, setSeoMetaTitle] = useState<ITranslation[]>([]);
  const [seoMetaDescription, setSeoMetaDescription] = useState<ITranslation[]>([]);
  const [seoMetaKeywords, setSeoMetaKeywords] = useState<ITranslation[]>([]);
  const [seoOgTitle, setSeoOgTitle] = useState<ITranslation[]>([]);
  const [seoOgDescription, setSeoOgDescription] = useState<ITranslation[]>([]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getLangs = useCallback(() => (countries?.items || []).map((c: any) => c.code), [countries]);

  const fillMissingLangs = useCallback(
    (arr: ITranslation[]): ITranslation[] => {
      const langs = getLangs();
      const existing = Array.isArray(arr) ? arr : [];
      return langs.map((lang) => existing.find((x) => x.language === lang) || { language: lang, translation: '' });
    },
    [getLangs]
  );

  const getTranslation = (arr: ITranslation[], lang: string) =>
    arr.find((x) => x.language === lang)?.translation || '';

  const setTranslation = (
    arr: ITranslation[],
    setArr: (v: ITranslation[]) => void,
    lang: string,
    value: string
  ) => {
    setArr(arr.map((x) => (x.language === lang ? { ...x, translation: value } : x)));
  };

  // ── Init from existing article ────────────────────────────────────────────

  useEffect(() => {
    if (!articleData || !countries?.items) return;

    setSymbol(articleData.symbol || '');
    setIsActive(articleData.is_active ?? true);

    const titleField = articleData.article_fields?.find((f: any) => f.symbol === FIELD_SYMBOL_TITLE);
    const subtitleField = articleData.article_fields?.find((f: any) => f.symbol === FIELD_SYMBOL_SUBTITLE);
    const imageField = articleData.article_fields?.find((f: any) => f.symbol === FIELD_SYMBOL_IMAGE);
    const contentField = articleData.article_fields?.find((f: any) => f.symbol === FIELD_SYMBOL_CONTENT);
    const popularField = articleData.article_fields?.find((f: any) => f.symbol === FIELD_SYMBOL_POPULAR);

    if (titleField) {
      setTitleFieldId(titleField.section_field_id);
      setTitleValues(fillMissingLangs(Array.isArray(titleField.value) ? titleField.value : []));
    }
    if (subtitleField) {
      setSubtitleFieldId(subtitleField.section_field_id);
      setSubtitleValues(fillMissingLangs(Array.isArray(subtitleField.value) ? subtitleField.value : []));
    }
    if (imageField) {
      setImageFieldId(imageField.section_field_id);
      setImageFile(imageField.file || []);
      setImageValues(Array.isArray(imageField.value) ? imageField.value : []);
    }
    if (contentField) {
      setContentFieldId(contentField.section_field_id);
      setContentValues(fillMissingLangs(Array.isArray(contentField.value) ? contentField.value : []));
    }
    if (popularField) {
      setPopularFieldId(popularField.section_field_id);
      const popVal = popularField.value;
      setPopular(popVal === true || popVal === 'true');
    }

    const tutorialField = articleData.article_fields?.find((f: any) => f.symbol === FIELD_SYMBOL_TUTORIAL);
    if (tutorialField) {
      setTutorialFieldId(tutorialField.section_field_id);
      const v = tutorialField.value;
      setTutorial(v === true || v === 'true');
    }

    const newsField = articleData.article_fields?.find((f: any) => f.symbol === FIELD_SYMBOL_NEWS);
    if (newsField) {
      setNewsFieldId(newsField.section_field_id);
      const v = newsField.value;
      setNews(v === true || v === 'true');
    }
  }, [articleData, countries, fillMissingLangs]);

  // ── Init SEO state from API ───────────────────────────────────────────────

  useEffect(() => {
    if (!seoItem || !countries?.items) return;
    setSeoUrlShortcut(fillMissingLangs(Array.isArray(seoItem.url_shortcut_array) ? seoItem.url_shortcut_array : []));
    setSeoH1(fillMissingLangs(Array.isArray(seoItem.h1) ? seoItem.h1 : []));
    setSeoMetaTitle(fillMissingLangs(Array.isArray(seoItem.meta_title) ? seoItem.meta_title : []));
    setSeoMetaDescription(fillMissingLangs(Array.isArray(seoItem.meta_description) ? seoItem.meta_description : []));
    setSeoMetaKeywords(fillMissingLangs(Array.isArray(seoItem.meta_keywords) ? seoItem.meta_keywords : []));
    setSeoOgTitle(fillMissingLangs(Array.isArray(seoItem.open_graph_title) ? seoItem.open_graph_title : []));
    setSeoOgDescription(fillMissingLangs(Array.isArray(seoItem.open_graph_description) ? seoItem.open_graph_description : []));
  }, [seoItem, countries, fillMissingLangs]);

  // ── Init empty state for new article ─────────────────────────────────────

  useEffect(() => {
    if (!isNew || !countries?.items) return;
    const langs = getLangs();
    const empty = langs.map((lang) => ({ language: lang, translation: '' }));
    setTitleValues(empty);
    setSubtitleValues(empty.map((x) => ({ ...x })));
    setContentValues(empty.map((x) => ({ ...x })));
  }, [isNew, countries, getLangs]);

  // ── Build payload ─────────────────────────────────────────────────────────

  const buildPayload = () => {
    const fileTemplate = (countries?.items || []).map((c: any) => ({
      language: c.code,
      url: null,
      is_active: null,
      name: null,
      description: null
    }));

    const resolveFieldId = (stateId: number | null, symbol: string) =>
      stateId ?? sectionFields?.items?.find((f: any) => f.symbol === symbol)?.id ?? null;

    const resolvedTitleId = resolveFieldId(titleFieldId, FIELD_SYMBOL_TITLE);
    const resolvedSubtitleId = resolveFieldId(subtitleFieldId, FIELD_SYMBOL_SUBTITLE);
    const resolvedImageId = resolveFieldId(imageFieldId, FIELD_SYMBOL_IMAGE);
    const resolvedContentId = resolveFieldId(contentFieldId, FIELD_SYMBOL_CONTENT);
    const resolvedPopularId = resolveFieldId(popularFieldId, FIELD_SYMBOL_POPULAR);
    const resolvedTutorialId = resolveFieldId(tutorialFieldId, FIELD_SYMBOL_TUTORIAL);
    const resolvedNewsId = resolveFieldId(newsFieldId, FIELD_SYMBOL_NEWS);

    const articleFields: any[] = [];

    if (resolvedTitleId) {
      articleFields.push({
        section_field_id: resolvedTitleId,
        type: 'STRING',
        symbol: FIELD_SYMBOL_TITLE,
        value: titleValues,
        blob_value: [],
        file: fileTemplate
      });
    }
    if (resolvedSubtitleId) {
      articleFields.push({
        section_field_id: resolvedSubtitleId,
        type: 'HTMLBLOCK',
        symbol: FIELD_SYMBOL_SUBTITLE,
        value: subtitleValues,
        file: fileTemplate
      });
    }
    if (resolvedImageId) {
      articleFields.push({
        section_field_id: resolvedImageId,
        type: 'IMAGE',
        symbol: FIELD_SYMBOL_IMAGE,
        value: imageValues,
        blob_value: [],
        file: imageFile
      });
    }
    if (resolvedContentId) {
      articleFields.push({
        section_field_id: resolvedContentId,
        type: 'HTMLBLOCK',
        symbol: FIELD_SYMBOL_CONTENT,
        value: contentValues,
        file: fileTemplate
      });
    }
    if (resolvedPopularId) {
      articleFields.push({
        section_field_id: resolvedPopularId,
        type: 'BOOL',
        symbol: FIELD_SYMBOL_POPULAR,
        value: popular ? 'true' : 'false',
        blob_value: [],
        file: fileTemplate
      });
    }
    if (resolvedTutorialId) {
      articleFields.push({
        section_field_id: resolvedTutorialId,
        type: 'BOOL',
        symbol: FIELD_SYMBOL_TUTORIAL,
        value: tutorial ? 'true' : 'false',
        blob_value: [],
        file: fileTemplate
      });
    }
    if (resolvedNewsId) {
      articleFields.push({
        section_field_id: resolvedNewsId,
        type: 'BOOL',
        symbol: FIELD_SYMBOL_NEWS,
        value: news ? 'true' : 'false',
        blob_value: [],
        file: fileTemplate
      });
    }

    return {
      section_id: SECTION_ID,
      symbol,
      title: titleValues,
      language,
      from_date: articleData?.from_date || null,
      to_date: articleData?.to_date || null,
      is_active: isActive,
      layouts: articleData?.layouts || 'Desktop|Mobile',
      article_fields: articleFields
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (isNew) {
      const result = await createArticle(payload as any);
      const newId = (result?.data as any)?.id;
      onSaved(newId);
    } else {
      await updateArticle(payload as any);
      onSaved();
    }
  };

  const handleSaveSeo = async () => {
    if (!seoItem) return;
    await updateSeo({
      url_shortcut: seoUrlShortcut,
      page_type: seoItem.page_type,
      params: seoItem.params,
      entity_id: seoItem.entity_id,
      entity_type: seoItem.entity_type,
      h1: seoH1,
      meta_title: seoMetaTitle,
      meta_description: seoMetaDescription,
      meta_keywords: seoMetaKeywords,
      open_graph_title: seoOgTitle,
      open_graph_description: seoOgDescription
    });
    await refetchSeo();
  };

  // ── Guard before render ───────────────────────────────────────────────────

  const isInitialized = isNew ? titleValues.length > 0 : contentValues.length > 0 || titleValues.length > 0;

  if (!isNew && (isLoading || !isInitialized)) {
    return <Loader />;
  }

  const currentTitle = getTranslation(titleValues, language);

  return (
    <div className={styles.wrapper}>

      {/* Page header */}
      <div className={styles.pageHeader}>
        <button className={styles.backBtn} onClick={onBack} title={t('Wróć')}>
          <ArrowLeft size={16} />
        </button>
        <div className={styles.headerMeta}>
          <h1 className={styles.pageTitle}>
            {isNew
              ? <Trans>Nowy artykuł</Trans>
              : (currentTitle || symbol || <Trans>Edytuj artykuł</Trans>)}
          </h1>
          {symbol && !isNew && (
            <p className={styles.pageSubtitle}>{symbol}</p>
          )}
        </div>

        {/* Language tabs */}
        <div className={styles.langTabs}>
          {(countries?.items || []).map((c: any) => (
            <button
              key={c.code}
              className={`${styles.langTab} ${language === c.code ? styles.langTabActive : ''}`}
              onClick={() => setLanguage(c.code)}>
              {c.code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* 2-column layout */}
      <div className={styles.editorLayout}>

        {/* ── Main column ─────────────────────────────────────────────────── */}
        <div className={styles.mainColumn}>

          {/* Card: Tytuł artykułu */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}><FileEarmarkText size={14} /></span>
              <h2 className={styles.cardTitle}><Trans>Tytuł artykułu</Trans></h2>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  <Trans>Tytuł</Trans> ({language.toUpperCase()})
                </label>
                <input
                  className={styles.input}
                  value={currentTitle}
                  placeholder={t('Wpisz tytuł artykułu...')}
                  onChange={(e) => setTranslation(titleValues, setTitleValues, language, e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Card: Podtytuł */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}><FileEarmarkText size={14} /></span>
              <h2 className={styles.cardTitle}>
                <Trans>Podtytuł</Trans> - {language.toUpperCase()}
              </h2>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.editorWrapper}>
                <TranslatedEditor
                  name={FIELD_SYMBOL_SUBTITLE}
                  value={subtitleValues}
                  selectedCountry={language}
                  setFieldValue={(_name: string, val: any) => setSubtitleValues(val)}
                  countries={countries?.items || []}
                />
              </div>
            </div>
          </div>

          {/* Card: Treść artykułu */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}><Globe size={14} /></span>
              <h2 className={styles.cardTitle}>
                <Trans>Treść artykułu</Trans> - {language.toUpperCase()}
              </h2>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.editorWrapper}>
                <TranslatedEditor
                  name={FIELD_SYMBOL_CONTENT}
                  value={contentValues}
                  selectedCountry={language}
                  setFieldValue={(_name: string, val: any) => setContentValues(val)}
                  countries={countries?.items || []}
                />
              </div>
            </div>
          </div>

        </div>

        {/* ── Side column ─────────────────────────────────────────────────── */}
        <div className={styles.sideColumn}>

          {/* Podgląd artykułu - tylko w trybie edycji */}
          {!isNew && seoItem && (() => {
            const slug =
              seoItem.url_shortcut_array?.find((x) => x.language === language)?.translation ||
              seoItem.url_shortcut_array?.[0]?.translation;
            const href = `/${language}/blog/${slug || seoItem.entity_id}`;
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.previewLink}>
                <BoxArrowUpRight size={13} />
                <Trans>Podgląd artykułu</Trans>
              </a>
            );
          })()}

          {/* Card: Ustawienia */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}><Gear size={14} /></span>
              <h2 className={styles.cardTitle}><Trans>Ustawienia</Trans></h2>
            </div>
            <div className={styles.cardBody}>
              <div
                className={styles.toggleRow}
                onClick={() => setIsActive(!isActive)}>
                <Checkbox checked={isActive} onClick={undefined} />
                <span className={styles.toggleLabel}>
                  {isActive ? <Trans>Artykuł aktywny</Trans> : <Trans>Artykuł nieaktywny</Trans>}
                </span>
              </div>
              <div
                className={styles.toggleRow}
                onClick={() => setPopular(!popular)}>
                <Checkbox checked={popular} onClick={undefined} />
                <span className={styles.toggleLabel}>
                  <Trans>Popularne</Trans>
                </span>
              </div>
              <div
                className={styles.toggleRow}
                onClick={() => setNews(!news)}>
                <Checkbox checked={news} onClick={undefined} />
                <span className={styles.toggleLabel}>
                  <Trans>Aktualności</Trans>
                </span>
              </div>
              <div
                className={styles.toggleRow}
                onClick={() => setTutorial(!tutorial)}>
                <Checkbox checked={tutorial} onClick={undefined} />
                <span className={styles.toggleLabel}>
                  <Trans>Baza wiedzy</Trans>
                </span>
              </div>
            </div>
          </div>

          {/* Card: Grafika */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}><ImageIcon size={14} /></span>
              <h2 className={styles.cardTitle}><Trans>Grafika</Trans></h2>
            </div>
            <div className={styles.cardBody}>
              <TranslatedFile
                name="image"
                file={imageFile}
                selectedCountry={language}
                setFieldValue={(_name: string, val: any) => setImageFile(val)}
                countries={countries?.items || []}
              />
            </div>
          </div>

          {/* Card: SEO / Symbol */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}><Globe size={14} /></span>
              <h2 className={styles.cardTitle}><Trans>SEO / Symbol</Trans></h2>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}><Trans>Symbol artykułu</Trans></label>
                <input
                  className={`${styles.input} ${styles.inputMono}`}
                  value={symbol}
                  placeholder="BLOG_ARTYKUL_1"
                  onChange={(e) =>
                    setSymbol(e.target.value.toUpperCase().replace(/\s/g, '_'))
                  }
                  disabled={!isNew}
                />
                {!isNew && (
                  <span className={styles.hint}>
                    <Trans>Symbol nie może być zmieniony po zapisaniu artykułu</Trans>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* SEO button - only in edit mode */}
          {!isNew && seoItem && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}><Globe size={14} /></span>
                <h2 className={styles.cardTitle}><Trans>SEO</Trans></h2>
              </div>
              <div className={styles.cardBody}>
                <p className={styles.seoPreviewUrl}>{seoItem.full_url}</p>
                <div className={styles.seoEditBtnWrapper}>
                  <Button color="secondary" onClick={() => setSeoModalOpen(true)}>
                    <Trans>Edytuj SEO</Trans>
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* SEO Modal */}
      {!isNew && seoItem && seoModalOpen && (
        <SeoModal
          language={language}
          countries={countries?.items || []}
          seoUrlShortcut={seoUrlShortcut}
          setSeoUrlShortcut={setSeoUrlShortcut}
          seoH1={seoH1}
          setSeoH1={setSeoH1}
          seoMetaTitle={seoMetaTitle}
          setSeoMetaTitle={setSeoMetaTitle}
          seoMetaDescription={seoMetaDescription}
          setSeoMetaDescription={setSeoMetaDescription}
          seoMetaKeywords={seoMetaKeywords}
          setSeoMetaKeywords={setSeoMetaKeywords}
          seoOgTitle={seoOgTitle}
          setSeoOgTitle={setSeoOgTitle}
          seoOgDescription={seoOgDescription}
          setSeoOgDescription={setSeoOgDescription}
          isSaving={isSavingSeo}
          onSave={handleSaveSeo}
          onClose={() => setSeoModalOpen(false)}
        />
      )}

      {/* Sticky save bar */}
      <div className={styles.saveBar}>
        <span className={styles.saveBarHint}>
          <Trans>Pamiętaj aby zapisać zmiany dla każdego języka osobno</Trans>
        </span>
        <div className={styles.saveBarButtons}>
          <Button color="gray" onClick={onBack}>
            <Trans>Anuluj</Trans>
          </Button>
          <Button color="primary" loading={isSaving || isCreating} onClick={handleSave}>
            <Trans>Zapisz artykuł</Trans>
          </Button>
        </div>
      </div>

    </div>
  );
};

export default BlogEditor;
